/**
 * Co-Lead Cash Call PDF generator.
 *
 * Self-contained, pure module (no Vue component deps) that builds a pdfMake
 * document definition for a CO-LEAD cash call spanning multiple keys/UMRs, and
 * a helper to turn it into a Blob for upload in the create-modal Send flow.
 *
 * Design intentionally mirrors the single-key PDF produced by
 * `generateDocDefinition(row, person)` in
 *   src/components/CashCall/cash-call-management-table/CashCallManagementTable.vue
 * (page margins, styles, the #faedbe cream section headers, the #FFF34D gold
 * totals bars, the tight table layout, the LimeIQ logo header, and the
 * Documents/Supporting-Information list) so the two PDFs read as one family.
 *
 * IMPORTANT: This does NOT touch the existing single-key generator. It is a
 * standalone util; all data is passed in via the args object.
 */

// pdfMake + vfs fonts imported EXACTLY as CashCallManagementTable.vue does
// (see that file, lines ~568-575).
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
// The LimeIQ logo, copied verbatim from the single-key generator (logoBase64),
// lives in its own module so this file stays readable (the base64 blob is ~57KB).
import { limeiqLogoBase64 } from './limeiqLogoBase64'

// works in both localhost and staging
pdfMake.vfs = (pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) || pdfFonts.vfs

/* ------------------------------------------------------------------ */
/* Shared style/layout constants (matched to the single-key PDF)       */
/* ------------------------------------------------------------------ */

const CREAM = '#faedbe' // section-header background
const GOLD = '#FFF34D' // totals-bar background
const MUTED = '#999' // "Not Attached" / greyed rows

// Copied from generateDocDefinition's `tightBoxLayout`.
const tightBoxLayout = {
  hLineWidth: () => 1,
  vLineWidth: () => 1,
  hLineColor: () => '#000',
  vLineColor: () => '#000',
  paddingTop: () => 2,
  paddingBottom: () => 2,
  paddingLeft: () => 5,
  paddingRight: () => 5
}

// Fallback documentation list, mirroring the app's upload UI
// (CASH_CALL_SUPPORTING_DOCUMENT_OPTIONS) so uploaded files - keyed by these
// same labels - actually match and show "Attached". Used only when the payload
// doesn't provide cashCall.document_types (which it normally does).
const DOCUMENT_TYPES = [
  'Adjuster Report',
  'Policy Including Certificate',
  'TPA Report including cash call amount',
  'Estimate',
  'Photos',
  'Weather / Fire / Police Report',
  'Engineer / Expert Report',
  'Sanction Check'
]

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const DASH = '—' // em dash "—"

/** Coerce anything to a trimmed string; null/undefined -> ''. */
function str(v) {
  return v === undefined || v === null ? '' : String(v).trim()
}

/** Parse a possibly comma-formatted numeric string/number to a float. */
function num(v) {
  if (v === '' || v === null || v === undefined) return 0
  const n = parseFloat(String(v).replace(/,/g, ''))
  return isNaN(n) ? 0 : n
}

/**
 * Digit-grouping money formatter matched to the single-key PDF's use of
 * Intl.NumberFormat. Prefix is configurable (default "$" — cash call amounts
 * are USD across the platform, matching the cash call list table).
 * @param {number} n
 * @param {string} prefix currency symbol
 * @param {number} decimals number of fraction digits (2 for totals, 0 inline)
 */
function formatMoney(n, prefix = '$', decimals = 2) {
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num(n))
  return `${prefix}${formatted}`
}

/** Distinct, non-empty values of a field across keys, in first-seen order. */
function distinct(keys, field) {
  const seen = []
  ;(keys || []).forEach((k) => {
    const v = str(k[field])
    if (v !== '' && !seen.includes(v)) seen.push(v)
  })
  return seen
}

/** Distinct values joined with a separator (default ", "). */
function distinctJoined(keys, field, sep = ', ') {
  const values = distinct(keys, field)
  return values.length ? values.join(sep) : DASH
}

/** First element of an array-or-scalar field (handles the [key] wrapping). */
function firstOf(value) {
  if (Array.isArray(value)) return value.length ? str(value[0]) : ''
  return str(value)
}

/** All brokers on a single key, joined (broker can be a multi-entry array). */
function brokersOf(value) {
  if (Array.isArray(value)) {
    return value.map((v) => str(v)).filter((v) => v !== '').join(', ')
  }
  return str(value)
}

/**
 * Distinct first-of-array values across keys for a wrapped field
 * (bdx_key / claimref are arrays of one), joined.
 */
function distinctJoinedFirst(keys, field, sep = ', ') {
  const seen = []
  ;(keys || []).forEach((k) => {
    const v = firstOf(k[field])
    if (v !== '' && !seen.includes(v)) seen.push(v)
  })
  return seen.length ? seen.join(sep) : DASH
}

/**
 * Distinct ALL values across keys for an array field (e.g. `broker`, which can
 * now hold multiple brokers per key), flattened and joined. Unlike
 * distinctJoinedFirst this keeps every entry, not just the first of each array.
 */
function distinctJoinedAll(keys, field, sep = ', ') {
  const seen = []
  ;(keys || []).forEach((k) => {
    const raw = k[field]
    const values = Array.isArray(raw) ? raw : (raw === undefined || raw === null || raw === '' ? [] : [raw])
    values.forEach((val) => {
      const v = str(val)
      if (v !== '' && !seen.includes(v)) seen.push(v)
    })
  })
  return seen.length ? seen.join(sep) : DASH
}

/** Turn "first.last@x.com" style emails into "First Last" (matched to single-key). */
function nameFromEmail(email) {
  const e = str(email)
  if (!e) return ''
  const username = e.split('@')[0]
  return username
    .split('.')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}

/** Normalize recipients arg into an array of email strings. */
function normalizeRecipients(recipients) {
  if (!recipients) return []
  const arr = Array.isArray(recipients) ? recipients : [recipients]
  return arr
    .map((r) => {
      if (typeof r === 'string') return r
      if (r && typeof r === 'object') return r.email || r.value || r.mail || ''
      return ''
    })
    .map(str)
    .filter((v) => v !== '')
}

/** Per-key cash call amount = round(total * share%)/... , no decimals inline. */
function keyAmount(totalAmount, sharePercentage) {
  const total = num(totalAmount)
  const share = num(sharePercentage)
  return Math.round((total * share) / 100)
}

/** A key needs funding unless funding_required is explicitly false. */
function isFunded(key) {
  return key.funding_required !== false
}

/* ------------------------------------------------------------------ */
/* Document definition builder                                         */
/* ------------------------------------------------------------------ */

/**
 * Build the pdfMake docDefinition for a co-lead cash call.
 *
 * @param {Object}   args
 * @param {Object}   args.cashCall       header/summary fields (see below)
 * @param {Array}    args.keys           co_lead_keys rows (one per UMR/key)
 * @param {Array}    args.recipients     selected recipient objects or emails
 * @param {Object}   args.requestor      { name, company, email }
 * @param {string}  [args.currencyPrefix='$']
 * @returns {Object} pdfMake document definition
 *
 * Expected `cashCall` fields (source names ASSUMED where noted in the report):
 *   reference, amount, comments,
 *   claim_loss_established (bool)  -> "Established Loss Fund"
 *   claim_reason                   -> "Cash Call Reason"
 *   claim_authority_scope (bool)   -> "Above Authority Payment?"
 *   sanctions_check                -> "Sanctions Check"
 *   indemnity, tpa_fees, adjusters_fees, defence_fees, expenses,
 *   attorney_coverage, total_indemnity, total_fee,
 *   supporting_documents: [{ document_type }]
 *
 * Expected `keys[i]` fields:
 *   bdx_key:[key], umr, rc, cc, sn, currency_type, claimref:[ref],
 *   underwriter_lead, broker:[...], tpa, coverholder, funding_required,
 *   share_percentage, insured_name, loss_state, loss_sl_state, loss_country,
 *   cause_of_loss, date_of_loss, date_claim_made, naic_code, year_of_account,
 *   initial_loss_fund_value, current_loss_fund_balance, broker_recipient
 */
export function buildColeadDocDefinition({
  cashCall = {},
  keys = [],
  recipients = [],
  requestor = {},
  currencyPrefix = '$'
} = {}) {
  const prefix = currencyPrefix || '$'
  const money2 = (n) => formatMoney(n, prefix, 2) // totals: 2 decimals
  const money0 = (n) => formatMoney(n, prefix, 0) // inline per-key: no decimals

  const reference = str(cashCall.reference) || DASH
  const totalCashCall = num(cashCall.amount)

  /* ---- header info block sources ---- */
  const managingAgent = distinctJoined(keys, 'underwriter_lead')
  const brokersJoined = distinctJoinedAll(keys, 'broker')
  const insuredName = distinctJoined(keys, 'insured_name')
  const dcaJoined = distinctJoined(keys, 'tpa')
  const coverholderJoined = distinctJoined(keys, 'coverholder')

  /* ---- recipient / requestor block ---- */
  const recipientEmails = normalizeRecipients(recipients)
  const recipientNames = recipientEmails.map(nameFromEmail)
  // Recipient Company = distinct broker companies joined (e.g. "Bowood / Acropolis").
  const recipientCompany = distinctJoinedFirst(keys, 'broker', ' / ')

  const requestorName =
    str(requestor.name) || nameFromEmail(requestor.email) || DASH
  const requestorCompany = str(requestor.company) || 'LimeIQ'
  const requestorEmail = str(requestor.email) || DASH

  /* ---- totals ---- */
  // Required sums over FUNDED keys only (an unticked key needs no funding), but
  // every selected key is shown in the breakdown regardless of funding.
  const totalRequired = (keys || [])
    .filter(isFunded)
    .reduce((sum, k) => sum + keyAmount(totalCashCall, k.share_percentage), 0)
  // Share % sums over ALL selected keys (funded + unfunded) so the TOTAL row's
  // share matches the full set of visible rows (should total 100%).
  const totalSharePct = (keys || [])
    .reduce((sum, k) => sum + num(k.share_percentage), 0)

  /* ---- documents ---- */
  const uploadedDocTypes = new Set(
    (cashCall.supporting_documents || []).map((d) => d && d.document_type)
  )
  const docStatus = (type) => ({
    text: uploadedDocTypes.has(type) ? 'Attached' : 'Not Attached',
    style: 'value',
    color: uploadedDocTypes.has(type) ? 'black' : MUTED
  })

  /* ---- small reusable cells ---- */
  const sectionHeaderCell = (text, colSpan = 2) => ({
    text,
    style: 'sectionHeader',
    colSpan,
    fillColor: CREAM,
    alignment: 'left'
  })

  const labelValueRow = (label, value) => [
    { text: label, style: 'label' },
    { text: value === '' || value === undefined || value === null ? DASH : value, style: 'value' }
  ]

  const line = {
    canvas: [{ type: 'line', x1: 0, y1: 0, x2: 520, y2: 0, lineWidth: 1 }],
    margin: [0, 5]
  }

  /* ---- two-column info rows (label left / value right, both sides) ---- */
  const infoRow = (labelL, valL, labelR, valR) => ({
    columns: [
      {
        width: '50%',
        columns: [
          {
            columns: [
              { width: 110, text: labelL, style: 'label' },
              { width: '*', text: valL || DASH, style: 'value' }
            ]
          }
        ]
      },
      {
        width: '50%',
        columns: [
          {
            columns: [
              { width: 110, text: labelR, style: 'label' },
              { width: '*', text: valR || DASH, style: 'value' }
            ]
          }
        ]
      }
    ],
    margin: [0, 1.5]
  })

  /* ---- gold totals box (two stacked rows) ---- */
  const goldTotalsBox = {
    table: {
      widths: ['*', 'auto'],
      body: [
        [
          {
            text: 'TOTAL CASH CALL AMOUNT',
            style: 'amountBarLabel',
            fillColor: GOLD,
            margin: [5, 3]
          },
          {
            text: money2(totalCashCall),
            style: 'amountBarValue',
            fillColor: GOLD,
            alignment: 'right',
            margin: [5, 3]
          }
        ],
        [
          {
            text: 'TOTAL CASH CALL REQUIRED',
            style: 'amountBarLabel',
            fillColor: GOLD,
            fontSize: 11,
            margin: [5, 3]
          },
          {
            text: money2(totalRequired),
            style: 'amountBarValue',
            fillColor: GOLD,
            fontSize: 11,
            alignment: 'right',
            margin: [5, 3]
          }
        ]
      ]
    },
    layout: tightBoxLayout
  }

  /* ---- Co-Lead UMRs breakdown table ---- */
  const breakdownHeader = [
    { text: 'UMR', style: 'label' },
    { text: 'Underwriter', style: 'label' },
    { text: 'Claim Ref', style: 'label' },
    { text: 'Broker Recipient', style: 'label' },
    { text: 'TPA', style: 'label' },
    { text: 'Share %', style: 'label', alignment: 'right' },
    { text: 'Amount', style: 'label', alignment: 'right' },
    { text: 'Required', style: 'label', alignment: 'right' }
  ]

  // The PDF is the single co-lead document and must show ALL selected keys and
  // their shares (LimeIQ + non-LimeIQ, funded + unticked). Funding is conveyed
  // per row instead of by exclusion: an unticked key is greyed out and its
  // Required column shows "—".
  const breakdownRows = (keys || []).map((k) => {
    const funded = isFunded(k)
    const amount = keyAmount(totalCashCall, k.share_percentage)
    const rowColor = funded ? 'black' : MUTED
    return [
      { text: str(k.umr) || DASH, style: 'value', color: rowColor },
      { text: str(k.underwriter_lead) || DASH, style: 'value', color: rowColor },
      { text: firstOf(k.claimref) || DASH, style: 'value', color: rowColor },
      // Prefer the explicitly selected broker recipient; fall back to the key's
      // broker company name so this column isn't just "—" when no specific
      // recipient contact was picked for the broker on this UMR.
      { text: str(k.broker_recipient) || brokersOf(k.broker) || DASH, style: 'value', color: rowColor },
      { text: str(k.tpa) || DASH, style: 'value', color: rowColor },
      {
        text: `${num(k.share_percentage)}%`,
        style: 'value',
        alignment: 'right',
        color: rowColor
      },
      {
        text: money0(amount),
        style: 'value',
        alignment: 'right',
        color: rowColor
      },
      {
        text: funded ? money0(amount) : DASH,
        style: 'value',
        alignment: 'right',
        color: rowColor
      }
    ]
  })

  const breakdownTotalRow = [
    { text: 'TOTAL', style: 'label' },
    { text: '', style: 'value' },
    { text: '', style: 'value' },
    { text: '', style: 'value' },
    { text: '', style: 'value' },
    {
      text: `${Math.round(totalSharePct * 100) / 100}%`,
      style: 'label',
      alignment: 'right'
    },
    { text: money2(totalCashCall), style: 'label', alignment: 'right' },
    { text: money2(totalRequired), style: 'label', alignment: 'right' }
  ]

  // The Co-Lead share/key breakdown is the one table that grows unbounded with
  // the number of UMRs. It is intentionally NOT `unbreakable`: when there are
  // more keys than fit on page 1, its rows flow cleanly onto page 2. `headerRows:
  // 1` makes pdfMake repeat the column header at the top of each page, and
  // `dontBreakRows: true` keeps any single row from being sliced in half across
  // the page boundary (a row jumps whole to the next page instead). This is what
  // lets the PDF extend to 2 pages when required to fit complete share details.
  const breakdownTable = {
    table: {
      headerRows: 1,
      dontBreakRows: true,
      widths: ['*', 'auto', 'auto', '*', 'auto', 'auto', 'auto', 'auto'],
      body: [breakdownHeader, ...breakdownRows, breakdownTotalRow]
    },
    layout: tightBoxLayout,
    margin: [0, 2]
  }

  /* ---- Account Details by key (right box, one row per key) ---- */
  // Label each row by its UMR (unique per key) rather than the underwriter,
  // which is usually identical across keys (e.g. every row "Brit") and so left
  // the rows indistinguishable.
  const accountDetailRows = (keys || []).map((k) => [
    { text: `${str(k.umr) || DASH} — Initial / Current`, style: 'label' },
    {
      text: `${money0(k.initial_loss_fund_value)} / ${money0(
        k.current_loss_fund_balance
      )}`,
      style: 'value'
    }
  ])

  /* ---- documentation rows ---- */
  // The document-type LABELS differ between the app's upload UI
  // (documentationOptions: "Adjuster Report", "Sanction Check", ...) and this
  // module's legacy DOCUMENT_TYPES fallback. Uploaded files are keyed by the
  // app's labels, so a hardcoded list here never matched them -> everything
  // showed "Not Attached". Render the app's list when the payload provides it
  // (cashCall.document_types) and only fall back to the legacy list otherwise.
  const documentTypeList =
    Array.isArray(cashCall.document_types) && cashCall.document_types.length
      ? cashCall.document_types
      : DOCUMENT_TYPES
  const documentRows = documentTypeList.map((type) => [
    { text: type, style: 'label' },
    docStatus(type)
  ])

  /* ---- Cash Call Details (left box, with gold request row) ---- */
  const cashCallDetailRows = [
    [sectionHeaderCell('Cash Call Details'), ''],
    labelValueRow('Indemnity', money2(cashCall.indemnity)),
    labelValueRow('TPA Fees', money2(cashCall.tpa_fees)),
    labelValueRow('Adjusters Fees', money2(cashCall.adjusters_fees)),
    labelValueRow('Defence Fees', money2(cashCall.defence_fees)),
    labelValueRow('Expenses', money2(cashCall.expenses)),
    labelValueRow('Attorney Coverage', money2(cashCall.attorney_coverage)),
    labelValueRow('Total Indemnity Amount', money2(cashCall.total_indemnity)),
    labelValueRow('Total Fees Amount', money2(cashCall.total_fee)),
    [
      { text: 'Cash Call Request Amount', bold: true, fillColor: GOLD },
      { text: money2(totalCashCall), bold: true, fillColor: GOLD, alignment: 'right' }
    ]
  ]

  /* ------------------------------------------------------------------ */
  /* Assemble document                                                  */
  /* ------------------------------------------------------------------ */
  return {
    // Balanced spacing tuned to fit a small co-lead on ONE page without looking
    // cramped, while allowing the report to extend to a 2nd page when there are
    // enough keys. The bottom summary boxes stay `unbreakable` (splitting a
    // small fixed box looks worse than moving it whole to page 2); only the
    // key-breakdown table is allowed to flow across pages. Bottom margin leaves
    // room for the page-number footer.
    pageMargins: [32, 18, 32, 28],
    // Page number footer - only meaningful (and only really seen) once the
    // content spills onto a 2nd page, but harmless on a 1-page doc.
    footer: (currentPage, pageCount) =>
      pageCount > 1
        ? {
            text: `Page ${currentPage} of ${pageCount}`,
            alignment: 'right',
            fontSize: 7,
            color: MUTED,
            margin: [0, 6, 34, 0]
          }
        : null,
    styles: {
      headerTitle: { fontSize: 18, bold: true, alignment: 'center', margin: [0, 1, 0, 1] },
      sectionHeader: { fontSize: 9, bold: true, margin: [0, 1, 0, 1] },
      label: { fontSize: 8, bold: true, margin: [0, 0.5, 0, 0.5] },
      label1: { fontSize: 6, bold: true, margin: [0, 0.5, 0, 0.5] },
      value: { fontSize: 8, margin: [0, 0.5, 0, 0.5] },
      amountBarLabel: { fontSize: 9, bold: true, fillColor: GOLD },
      amountBarValue: { fontSize: 9, bold: true, fillColor: GOLD, alignment: 'right' }
    },
    defaultStyle: { fontSize: 9 },
    content: [
      /* HEADER */
      {
        columns: [
          { image: limeiqLogoBase64, width: 85, height: 28 },
          {
            text: 'Cash Call Request - ' + reference,
            alignment: 'right',
            bold: true,
            fontSize: 12,
            margin: [0, 12, 10, 0]
          }
        ]
      },

      line,

      /* INFO BLOCK (2 columns) */
      infoRow('Managing Agent', managingAgent, 'DCA', dcaJoined),
      infoRow('Broker', brokersJoined, 'Coverholder', coverholderJoined),
      {
        columns: [
          {
            width: '50%',
            columns: [
              {
                columns: [
                  { width: 110, text: 'Insured Name', style: 'label' },
                  { width: '*', text: insuredName, style: 'value' }
                ]
              }
            ]
          },
          { width: '50%', text: '' }
        ],
        margin: [0, 1]
      },

      { text: ' ', fontSize: 4, margin: [0, 2] },

      /* RECIPIENT / REQUESTOR BLOCK (2 columns) */
      infoRow(
        'Recipient Name',
        { text: recipientNames.length ? recipientNames.join('\n') : DASH, style: 'value' },
        'Requestor Name',
        requestorName
      ),
      infoRow('Recipient Company', recipientCompany, 'Requestor Company', requestorCompany),
      infoRow(
        'Recipient Email',
        { text: recipientEmails.length ? recipientEmails.join('\n') : DASH, style: 'value' },
        'Requestor Email',
        requestorEmail
      ),

      { text: ' ', fontSize: 4, margin: [0, 2] },

      /* GOLD TOTALS BOX */
      goldTotalsBox,

      { text: ' ', fontSize: 4, margin: [0, 2] },

      /* CO-LEAD UMRs — Share & Funding Breakdown */
      { text: 'Co-Lead UMRs — Share & Funding Breakdown', style: 'sectionHeader' },
      breakdownTable,

      { text: ' ', fontSize: 4, margin: [0, 2] },

      /* CASH CALL COMMENTS (left summary / right free-text) */
      {
        unbreakable: true,
        columns: [
          {
            width: '50%',
            table: {
              headerRows: 1,
              widths: ['55%', '45%'],
              body: [
                [sectionHeaderCell('Cash Call Comments'), ''],
                labelValueRow(
                  'Established Loss Fund:',
                  cashCall.claim_loss_established ? 'Yes' : 'No'
                ),
                labelValueRow('Cash Call Reason:', str(cashCall.claim_reason) || DASH),
                labelValueRow(
                  'Above Authority Payment?:',
                  cashCall.claim_authority_scope ? 'Yes' : 'No'
                )
              ]
            },
            layout: tightBoxLayout
          },
          {
            width: '50%',
            table: {
              headerRows: 1,
              widths: ['*'],
              body: [
                [sectionHeaderCell('Cash Call Comments', 1)],
                [{ text: str(cashCall.comments), style: 'value', margin: [5, 5, 5, 5] }]
              ]
            },
            layout: tightBoxLayout
          }
        ],
        columnGap: 20,
        margin: [0, 2, 0, 2]
      },

      { text: ' ', fontSize: 4, margin: [0, 2] },

      /* BDX DETAILS + CLAIM DETAILS */
      {
        unbreakable: true,
        columns: [
          {
            width: '50%',
            table: {
              headerRows: 1,
              widths: ['35%', '65%'],
              body: [
                [sectionHeaderCell('BDX Details'), ''],
                // Actual per-UMR values, distinct + comma-separated across every
                // selected key (two keys -> "A, B"; three -> "A, B, C").
                labelValueRow('UMR', distinctJoined(keys, 'umr')),
                labelValueRow('Risk Code', distinctJoined(keys, 'rc')),
                labelValueRow('CAT Code', distinctJoined(keys, 'cc')),
                labelValueRow('Section #', distinctJoined(keys, 'sn')),
                labelValueRow(
                  'Currency / YOA',
                  `${distinctJoined(keys, 'currency_type')} / ${distinctJoined(
                    keys,
                    'year_of_account'
                  )}`
                )
              ]
            },
            layout: tightBoxLayout
          },
          {
            width: '50%',
            table: {
              headerRows: 1,
              widths: ['35%', '65%'],
              body: [
                [sectionHeaderCell('Claim Details'), ''],
                labelValueRow('DCA Claim Reference', distinctJoinedFirst(keys, 'claimref')),
                labelValueRow('Sanctions Check', str(cashCall.sanctions_check) || DASH),
                labelValueRow('Loss Country', distinctJoined(keys, 'loss_country')),
                // Loss / Policy State are US-only, matching the claim step (shown
                // only for USA). States auto-filled from the BDX stay on the keys
                // when the country is changed, so don't print them for another
                // country. No country (older records) keeps both rows.
                ...(distinct(keys, 'loss_country').length === 0 || distinct(keys, 'loss_country').includes('USA')
                  ? [
                      labelValueRow('Loss State', distinctJoined(keys, 'loss_state')),
                      labelValueRow('Policy State', distinctJoined(keys, 'loss_sl_state'))
                    ]
                  : []),
                labelValueRow('Loss Description', distinctJoined(keys, 'cause_of_loss'))
              ]
            },
            layout: tightBoxLayout
          }
        ],
        columnGap: 20,
        margin: [0, 2]
      },

      { text: ' ', fontSize: 4, margin: [0, 2] },

      /* PROGRAM DETAILS + ACCOUNT DETAILS BY KEY */
      {
        unbreakable: true,
        columns: [
          {
            width: '50%',
            table: {
              widths: ['*', '*'],
              body: [
                [sectionHeaderCell('Program Details'), ''],
                // Binder/Policy inception & expiry are not captured per-UMR in the
                // co-lead flow, so they show the em dash. Date of Loss / Date Claim
                // Made are real per-UMR values, distinct + comma-separated.
                labelValueRow('Binder Inception / Expiration', DASH),
                labelValueRow('Policy Inception / Expiration', DASH),
                labelValueRow('Date of Loss', distinctJoined(keys, 'date_of_loss')),
                labelValueRow('Date Claim Made', distinctJoined(keys, 'date_claim_made'))
              ]
            },
            layout: tightBoxLayout
          },
          {
            width: '50%',
            table: {
              widths: ['*', 'auto'],
              body: [
                [sectionHeaderCell('Account Details — By Key'), ''],
                ...(accountDetailRows.length
                  ? accountDetailRows
                  : [[{ text: DASH, style: 'value', colSpan: 2 }, '']])
              ]
            },
            layout: tightBoxLayout
          }
        ],
        columnGap: 20
      },

      { text: ' ', fontSize: 4, margin: [0, 2] },

      /* CASH CALL DETAILS + DOCUMENTS */
      {
        unbreakable: true,
        columns: [
          {
            width: '50%',
            table: {
              widths: ['*', 'auto'],
              body: cashCallDetailRows
            },
            layout: tightBoxLayout
          },
          {
            width: '50%',
            table: {
              widths: ['*', 'auto'],
              body: [
                [sectionHeaderCell('Documents / Supporting Information'), ''],
                ...documentRows
              ]
            },
            layout: tightBoxLayout
          }
        ],
        columnGap: 20
      }
    ]
  }
}

/**
 * Generate a Blob from the co-lead docDefinition. Same pattern as the single-key
 * `generatePdfBlob`: pdfMake.createPdf(...).getBlob(resolve).
 * @param {Object} args same shape as buildColeadDocDefinition
 * @returns {Promise<Blob>}
 */
export function generateColeadPdfBlob(args) {
  return new Promise((resolve) => {
    pdfMake.createPdf(buildColeadDocDefinition(args)).getBlob(resolve)
  })
}

export default { buildColeadDocDefinition, generateColeadPdfBlob }
